import React, { useEffect, useMemo, useState } from "react";
import { db, storage } from "../firebaseConfig";
import {
  addDoc, collection, getDocs, orderBy, query, serverTimestamp,
  where, limit
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/* ----- tiny helpers (in-file) ----- */
const CATEGORIES = ["Bye-laws", "Audit", "AGM", "General"];
const newId = () => `CIO-${new Date().getFullYear()}-${Math.random().toString().slice(2,6)}`;
async function uploadFiles(requestId, files=[]) {
  const out = [];
  for (const f of files) {
    const path = `cio/requests/${requestId}/${Date.now()}_${f.name.replace(/\s+/g,"_")}`;
    const r = ref(storage, path);
    await uploadBytes(r, f, { contentType: f.type || "application/octet-stream" });
    out.push({ name:f.name, size:f.size||0, url: await getDownloadURL(r) });
  }
  return out;
}

export default function CIOPortal() {
  const [tab, setTab] = useState("home"); // home | submit | track | faq
  return (
    <div className="min-h-screen bg-slate-50">
      {/* NAVBAR */}
      <nav className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="font-extrabold text-indigo-700"></div>
          <div className="flex items-center gap-4 text-sm">
            <button className={`px-2 py-1 rounded ${tab==="home"?"text-indigo-700 font-semibold":""}`} onClick={()=>setTab("home")}>CIO Home</button>
            <button className={`px-2 py-1 rounded ${tab==="submit"?"text-indigo-700 font-semibold":""}`} onClick={()=>setTab("submit")}>Submit Request</button>
            <button className={`px-2 py-1 rounded ${tab==="track"?"text-indigo-700 font-semibold":""}`} onClick={()=>setTab("track")}>Track</button>
            <button className={`px-2 py-1 rounded ${tab==="faq"?"text-indigo-700 font-semibold":""}`} onClick={()=>setTab("faq")}>FAQ</button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {tab==="home"   && <HomeIntro />}
        {tab==="submit" && <SubmitForm />}
        {tab==="track"  && <TrackCard />}
        {tab==="faq"    && <FAQ />}
      </main>
    </div>
  );
}

/* ---------- Home ---------- */
function HomeIntro() {
  return (
    <section>
      <h1 className="text-3xl md:text-4xl font-extrabold text-slate-800">CIO Portal</h1>
      <p className="text-slate-600 mt-2">
        Online Information Requests • Transparency • Compliance (MSCS Act Sec. 106 / Rule 35).
      </p>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Tile title="Submit Request" desc="Ask for bye-laws, audit, AGM documents etc." />
        <Tile title="Track Status" desc="Use Request ID & last 6 digits of mobile." />
        <Tile title="Download Replies" desc="CIO reply PDFs available on closure." />
      </div>

      <div className="bg-white rounded-xl border p-4 mt-6">
        <h3 className="font-bold mb-2">Downloads</h3>
        <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
          <li><a className="text-indigo-700" href="#">Bye-laws (PDF)</a></li>
          <li><a className="text-indigo-700" href="#">MSCS Act & Rules (Link)</a></li>
          <li><a className="text-indigo-700" href="#">Latest Audit Report (PDF)</a></li>
        </ul>
      </div>
    </section>
  );
}
function Tile({title,desc}) {
  return (
    <div className="bg-white rounded-xl border p-4">
      <div className="font-bold">{title}</div>
      <p className="text-sm text-slate-600">{desc}</p>
    </div>
  );
}

/* ---------- Submit ---------- */
function SubmitForm() {
  const [f, setF] = useState({
    memberName:"", membershipNo:"", email:"", mobile:"",
    category:"General", subject:"", description:""
  });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e){
    e.preventDefault(); setBusy(true);
    try{
      const requestId = newId();
      const attachments = await uploadFiles(requestId, files);
      await addDoc(collection(db,"requests"), {
        ...f,
        requestId, attachments,
        status:"New",
        dateSubmitted: serverTimestamp(),
        dueDate: new Date(Date.now()+30*24*3600*1000).toISOString(),
      });
      alert(`✅ Request submitted. Your Request ID is ${requestId}`);
      setF({memberName:"",membershipNo:"",email:"",mobile:"",category:"General",subject:"",description:""});
      setFiles([]);
    }catch(err){ alert("❌ Failed: "+(err?.message||err)); }
    finally{ setBusy(false); }
  }

  return (
    <section>
      <h2 className="text-2xl font-bold mb-3">Submit Information Request</h2>
      <form onSubmit={onSubmit} className="bg-white rounded-xl border p-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <Input label="Member Name *" value={f.memberName} onChange={v=>setF({...f,memberName:v})} required/>
          <Input label="Membership No." value={f.membershipNo} onChange={v=>setF({...f,membershipNo:v})}/>
          <Input label="Email *" type="email" value={f.email} onChange={v=>setF({...f,email:v})} required/>
          <Input label="Mobile *" value={f.mobile} onChange={v=>setF({...f,mobile:v})} required/>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Select label="Information Category" value={f.category} onChange={v=>setF({...f,category:v})} options={CATEGORIES}/>
          <Input label="Subject *" value={f.subject} onChange={v=>setF({...f,subject:v})} required/>
        </div>
        <TextArea label="Description *" rows={5} value={f.description} onChange={v=>setF({...f,description:v})} required/>
        <div>
          <label className="text-sm font-medium">Upload supporting file (optional)</label>
          <input multiple accept=".pdf,.jpg,.png" type="file" onChange={e=>setFiles(Array.from(e.target.files||[]))} className="block mt-1"/>
        </div>
        <button disabled={busy} className={`px-4 py-2 rounded text-white ${busy?"bg-indigo-400":"bg-indigo-600 hover:bg-indigo-700"}`}>
          {busy?"Submitting…":"Submit Request"}
        </button>
      </form>
    </section>
  );
}

/* ---------- Track ---------- */
function TrackCard(){
  const [requestId,setRequestId]=useState(""); 
  const [mobile,setMobile]=useState("");
  const [info,setInfo]=useState(null); const [busy,setBusy]=useState(false);

  async function find(e){
    e.preventDefault(); setBusy(true);
    try{
      const qy = query(collection(db,"requests"), where("requestId","==",requestId), limit(1));
      const snap = await getDocs(qy);
      if (snap.empty) { setInfo(null); alert("Not found"); }
      else {
        const d = snap.docs[0].data();
        const ok = (d.mobile||"").slice(-6) === String(mobile).slice(-6);
        if (!ok) { setInfo(null); alert("Mobile mismatch"); }
        else setInfo(d);
      }
    }catch(err){ alert("Failed: "+(err?.message||err)); }
    finally{ setBusy(false); }
  }

  return (
    <section>
      <h2 className="text-2xl font-bold mb-3">Track Your Request</h2>
      <form onSubmit={find} className="bg-white rounded-xl border p-5 space-y-3 max-w-xl">
        <Input label="Request ID" value={requestId} onChange={setRequestId} placeholder="e.g., CIO-2025-1234"/>
        <Input label="Registered Mobile (last 6 digits)" value={mobile} onChange={setMobile} />
        <button disabled={busy} className={`px-4 py-2 rounded text-white ${busy?"bg-indigo-400":"bg-indigo-600 hover:bg-indigo-700"}`}>{busy?"Checking…":"Check Status"}</button>
      </form>

      {info && (
        <div className="bg-white rounded-xl border p-5 mt-4">
          <div className="font-bold">{info.subject}</div>
          <div className="text-sm text-slate-600">Status: <StatusBadge s={info.status}/></div>
          <div className="text-sm text-slate-600">Due by: {new Date(info.dueDate).toDateString()}</div>
          {info.replyDocURL ? (
            <a href={info.replyDocURL} target="_blank" rel="noreferrer" className="inline-block mt-3 px-3 py-2 rounded bg-emerald-600 text-white">View Reply PDF</a>
          ) : <div className="text-xs text-slate-500 mt-2">Reply not uploaded yet.</div>}
        </div>
      )}
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ(){
  return (
    <section className="bg-white rounded-xl border p-5">
      <h2 className="text-xl font-bold mb-2">FAQ & Guidelines</h2>
      <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
        <li>Information is supplied within 30 days (MSCS Rule 35).</li>
        <li>Use Request ID to track & download CIO reply PDF.</li>
        <li>Appeal: CEO/Board as per bye-laws.</li>
      </ul>
    </section>
  );
}

/* ---------- small UI bits ---------- */
function Input({label,required=false,value,onChange,placeholder,type="text"}) {
  return (
    <label className="text-sm block">
      <span className="font-medium">{label}</span>{required? "":""}
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        className="w-full mt-1 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
    </label>
  );
}
function TextArea({label,value,onChange,rows=4,required=false}) {
  return (
    <label className="text-sm block">
      <span className="font-medium">{label}</span>{required? "":""}
      <textarea rows={rows} value={value} onChange={e=>onChange(e.target.value)}
        className="w-full mt-1 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
    </label>
  );
}
function Select({label,value,onChange,options=[]}) {
  return (
    <label className="text-sm block">
      <span className="font-medium">{label}</span>
      <select value={value} onChange={e=>onChange(e.target.value)}
        className="w-full mt-1 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
        {options.map(o=><option key={o}>{o}</option>)}
      </select>
    </label>
  );
}
function StatusBadge({s}) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs border
      ${s==="Closed"?"bg-emerald-50 text-emerald-800 border-emerald-200":
        s==="In Progress"?"bg-amber-50 text-amber-800 border-amber-200":
        s==="Rejected"?"bg-rose-50 text-rose-800 border-rose-200":"bg-slate-50 text-slate-700 border-slate-200"}`}>{s}</span>
  );
}
